// ShizaiApprovalsSimulatorCard — "承認シミュレータ" (iter_143e P2/v4).
//
// Lets admins type a hypothetical PO (dept + amount + priority + 受注分類)
// and instantly see which approval rule fires + who the effective approver
// would be after delegation is applied. Every rule is evaluated, so misses
// come back with per-field ✓/✗ reasoning — useful when rule ordering is
// giving unexpected results.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChartLineUp, Play, Check, X, ArrowRight, ArrowsClockwise, Warning, User, PaperPlaneTilt,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const PRIORITY_OPTIONS = [
  { value: 'low',    label: '低' },
  { value: 'normal', label: '通常' },
  { value: 'high',   label: '高' },
  { value: 'urgent', label: '緊急' },
];

export default function ShizaiApprovalsSimulatorCard() {
  const [form, setForm] = useState({
    department_id: '',
    total: 100000,
    priority: 'normal',
    order_category_code: '',
    order_type: '',
  });
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/mfg/departments`, { withCredentials: true })
      .then((r) => setDepartments(r.data || [])).catch(() => {});
    axios.get(`${API}/shizai/order-categories`, { withCredentials: true })
      .then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/shizai/approvals/simulate`, {
        department_id: form.department_id || null,
        total: Number(form.total || 0),
        priority: form.priority,
        order_category_code: form.order_category_code || null,
        order_type: form.order_type || null,
      }, { withCredentials: true });
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'シミュレーション失敗');
    } finally {
      setLoading(false);
    }
  };

  const matched = result?.matched_rule;
  const effective = result?.effective_approver;
  const delegation = result?.delegation;
  const rules = result?.rules_evaluated || [];

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 mt-4" data-testid="approvals-simulator-card">
      <div className="text-sm font-bold text-zinc-800 flex items-center gap-1.5 mb-1">
        <ChartLineUp weight="duotone" className="text-purple-500" size={18} />
        承認シミュレータ
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">
        仮の発注情報を入力すると、どのルールが適用され、誰が最終承認者になるかを確認できます。ルール順を変更する前の検証に便利です。
      </p>

      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] text-zinc-500">部署</Label>
          <Select value={form.department_id || '__any__'} onValueChange={(v) => setForm({ ...form, department_id: v === '__any__' ? '' : v })}>
            <SelectTrigger className="h-9 mt-0.5" data-testid="sim-dept-select">
              <SelectValue placeholder="部署を選択…" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="__any__">— 未指定 —</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">金額（¥）</Label>
          <Input
            type="number"
            min={0}
            step={10000}
            value={form.total}
            onChange={(e) => setForm({ ...form, total: e.target.value })}
            className="h-9 mt-0.5 font-mono"
            data-testid="sim-total-input"
          />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">優先度</Label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="h-9 mt-0.5" data-testid="sim-priority-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">受注分類（任意）</Label>
          <Select value={form.order_category_code || '__any__'} onValueChange={(v) => setForm({ ...form, order_category_code: v === '__any__' ? '' : v })}>
            <SelectTrigger className="h-9 mt-0.5" data-testid="sim-category-select">
              <SelectValue placeholder="分類を選択…" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="__any__">— 未指定 —</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.name || c.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        onClick={run}
        disabled={loading}
        className="mt-3 w-full bg-teal-700 hover:bg-teal-800"
        data-testid="sim-run-btn"
      >
        {loading ? (
          <><ArrowsClockwise className="animate-spin mr-1" size={14} />評価中…</>
        ) : (
          <><Play weight="fill" size={14} className="mr-1" />シミュレート実行</>
        )}
      </Button>

      {/* Result */}
      {result && (
        <div className="mt-4 border-t border-zinc-200 pt-3" data-testid="sim-result">
          {matched ? (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="sim-result-matched">
              <div className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
                <Check weight="fill" size={14} />
                ヒット: {matched.name} <span className="opacity-60">(#{matched.sort_order}, idle={matched.idle_days}日)</span>
              </div>
              <div className="mt-1.5 text-xs text-zinc-700 flex items-center gap-1.5">
                <User size={12} />
                承認者: <span className="font-mono">{effective?.email}</span>
                {matched.fallback_email && (
                  <>
                    <ArrowRight size={10} className="text-zinc-400" />
                    <span className="text-zinc-500 font-mono">(fallback: {matched.fallback_email})</span>
                  </>
                )}
              </div>
              {delegation && (
                <div className="mt-1.5 text-[11px] text-violet-700 flex items-center gap-1.5">
                  <PaperPlaneTilt weight="fill" size={11} />
                  委任中: {delegation.from} → {delegation.to} ({delegation.from_date}〜{delegation.to_date})
                  {delegation.note && <span className="italic text-violet-500">· {delegation.note}</span>}
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="sim-result-no-match">
              <Warning weight="fill" size={12} className="inline mr-1" />
              条件に一致するルールがありません。手動承認が必要です。
            </div>
          )}

          {/* Per-rule breakdown */}
          <details className="mt-3">
            <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-700" data-testid="sim-eval-toggle">
              全ルールの評価を見る（{rules.length}件）
            </summary>
            <div className="mt-2 space-y-1.5">
              {rules.map((r) => (
                <div
                  key={r.id}
                  className={`rounded-lg border p-2 ${r.matched ? 'bg-emerald-50 border-emerald-200' : 'bg-zinc-50 border-zinc-200'}`}
                  data-testid={`sim-eval-rule-${r.id}`}
                >
                  <div className="text-xs font-semibold text-zinc-800 flex items-center gap-1">
                    {r.matched ? <Check size={12} className="text-emerald-600" /> : <X size={12} className="text-rose-400" />}
                    #{r.sort_order} {r.name} <span className="text-zinc-400 font-normal font-mono">· {r.approver_email}</span>
                  </div>
                  <ul className="mt-1 text-[11px] space-y-0.5">
                    {r.reasons.map((rn, i) => (
                      <li key={i} className={rn.pass ? 'text-emerald-700' : 'text-rose-700'}>
                        {rn.pass ? '✓' : '✗'} {rn.field}: {rn.why}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
